import type { Value } from '@udecode/plate-common';
import { getHandler } from '@udecode/plate-common';
import type { TMentionElement } from '@udecode/plate-mention';
import type { StyledElementProps } from '@udecode/plate-styled-components';
import { getRootProps } from '@udecode/plate-styled-components';
import { useFocused, useSelected } from 'slate-react';
import styled from 'styled-components';
import tw from 'twin.macro';

export const CustomMentionElement = <V extends Value>(
  props: MentionElementProps<V>
) => {
  const {
    attributes,
    children,
    nodeProps,
    element,
    prefix,
    onClick,
    renderLabel,
  } = props;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rootProps = getRootProps(props) as any;

  const selected = useSelected();
  const focused = useFocused();

  return (
    <StyledSpan
      selected={selected}
      focused={focused}
      {...attributes}
      data-slate-value={element.mention_value}
      contentEditable={false}
      onClick={getHandler(onClick, element)}
      {...rootProps}
      {...nodeProps}
    >
      {prefix}
      {renderLabel ? renderLabel(element) : `@${element.mention_value}`}
      {children}
    </StyledSpan>
  );
};

// renderElement props
type MentionElementProps<V extends Value> = StyledElementProps<
  V,
  TMentionElement
> & {
  /**
   * Prefix rendered before mention
   */
  prefix?: string;
  onClick?: (mentionNode: unknown) => void;
  renderLabel?: (mentionable: TMentionElement) => string;
};

const StyledSpan = styled.span<StyledSpanProps>`
  ${tw`my-0 mx-px inline-block text-gray-700 text-sm rounded font-bold`}
  ${({ selected, focused }) =>
    selected && focused && tw`ring-2 ring-emerald-900`}
`;

type StyledSpanProps = {
  selected?: boolean;
  focused?: boolean;
};
